import type { AgentRoomFeedItemView, MissionEvidenceItemView } from "../api";

/**
 * OWNEXP Slice 6 — Missions-Phasen-Streifen (R-MR-2): Intake→Scope→Ready→
 * Lease→Ausführung→Tests→Prüfung→Deploy→Beobachtung→Abschluss.
 *
 * Honestitssvertrag: Jede Phase wird NUR aus realen Signalen abgeleitet
 * (Mission-Row, Queue-Item-Zustand, Writer-Lease, Run-Events, Evidence-Kinds,
 * Findings). Phasen ohne separates Messsignal der Runtime werden als
 * „kein Signal" markiert — nie als erreicht, nie als 0 vorgetäuscht (DC-12).
 * Kein CoT: Metadaten reichen, keine Token-Ströme.
 */

export type PhasenZustand = "erreicht" | "aktiv" | "ausstehend" | "kein_signal";

export interface PhasenSchritt {
  id: string;
  label: string;
  zustand: PhasenZustand;
  zeitstempel: string | null;
  hinweis: string;
}
export interface PhasenEingabe {
  mission: { status?: string; created_at?: string; updated_at?: string } | null;
  items: Array<{
    queue_state?: string | null;
    queue_state_reason?: string | null;
    scope_verified?: number | null;
    writer_scope?: string | null;
    lease_holder?: string | null;
    lease_acquired_at?: string | null;
    lease_expires_at?: string | null;
  }>;
  feed: AgentRoomFeedItemView[];
  evidence: MissionEvidenceItemView[];
  findings: Array<{ found_by?: string | null; status?: string | null }>;
}

const TERMINAL_GUT = new Set(["completed", "COMPLETED", "done", "DONE"]);
const WARTEZUSTAENDE = new Set(["READY", "READY_TO_RESUME", "RUNNING"]);

function runEventZeitpunkt(feed: AgentRoomFeedItemView[], eventType: string): string | null {
  const treffer = feed.filter((f) => f.kind === "run_event" && f.type === eventType);
  if (treffer.length === 0) return null;
  return treffer.map((f) => f.occurredAt).sort().at(-1) ?? null;
}

export function missionsPhasen(eingabe: PhasenEingabe): PhasenSchritt[] {
  const { mission, items, feed, evidence, findings } = eingabe;
  const missionStatus = String(mission?.status ?? "");
  const abgeschlossen = TERMINAL_GUT.has(missionStatus);

  // Aufnahme: Mission existiert = aufgenommen (Zeitpunkt aus created_at).
  const aufnahme = String(mission?.created_at ?? "") || null;

  // Umfang: Scope-Verifikation der Teilaufgaben (writer_scope gesetzt + verified).
  const mitScope = items.filter((it) => it.writer_scope);
  const scopeVerifiziert = mitScope.some((it) => it.scope_verified === 1);
  const scopeOffen = mitScope.length > 0 && !scopeVerifiziert;

  // Bereit: Item hat Startzustand erreicht (READY/READY_TO_RESUME/RUNNING zählt als passiert).
  const bereitItem = items.find((it) => WARTEZUSTAENDE.has(String(it.queue_state ?? "")));
  const bereit = bereitItem ? String(bereitItem.queue_state) : null;

  // Lease: aktueller Writer-Holder auf einem Item.
  const leaseItem = items.find((it) => it.lease_holder);
  const laeuft = items.some((it) => String(it.queue_state ?? "") === "RUNNING");

  // Ausführung: run.started-Event oder aktuelles RUNNING.
  const startedZeit = runEventZeitpunkt(feed, "run.started");

  // Tests/Prüfung/Deploy/Beobachtung: NUR aus Evidence-Kinds bzw. Findings
  // ableiten — die Runtime hat kein separates Phasen-Signal; ohne Beleg
  // bleibt die Phase ehrlich „kein Signal".
  const kinds = evidence.map((e) => String(e.evidenceKind ?? ""));
  const hatKind = (muster: RegExp): string | null =>
    kinds.find((k) => muster.test(k)) ?? null;
  const testKind = hatKind(/test/i);
  const verifiziertDurchFinding = findings.some(
    (f) => /verif|pruef|review/i.test(String(f.found_by ?? "")) && f.status !== "WONT_FIX",
  );
  // CP-16: „Prüfung erreicht" verlangt einen inhaltlich verifizierten Beleg
  // (contentBinding=VERIFIED_CONTENT) — REFERENCE_ONLY/UNKNOWN sind keine
  // Verifikation des Inhalts und bleiben ehrlich „kein Signal".
  const verifizierterBeleg = evidence.find(
    (e) =>
      String(e.contentBinding ?? "") === "VERIFIED_CONTENT" &&
      /verif|review|acceptance|test|verdict|gate/i.test(String(e.evidenceKind ?? "")),
  );
  const pruefKind = hatKind(/verif|review|acceptance/i);
  const deployKind = hatKind(/deploy|release|rollout/i);
  const observKind = hatKind(/observ|telemetry|monitor/i);

  const abschlussZeit =
    runEventZeitpunkt(feed, "run.completed") ?? (abgeschlossen ? String(mission?.updated_at ?? "") || null : null);
  const fehlgeschlagen =
    items.some((it) => String(it.queue_state ?? "") === "FAILED") ||
    runEventZeitpunkt(feed, "run.failed") !== null;

  const schritt = (
    id: string,
    label: string,
    zustand: PhasenZustand,
    hinweis: string,
    zeitstempel: string | null = null,
  ): PhasenSchritt => ({ id, label, zustand, zeitstempel, hinweis });

  return [
    schritt(
      "intake",
      "Aufnahme",
      aufnahme ? "erreicht" : "ausstehend",
      aufnahme ? "Mission über den kanonischen Intake aufgenommen." : "Kein Aufnahme-Zeitpunkt gemessen.",
      aufnahme,
    ),
    schritt(
      "scope",
      "Umfang",
      scopeVerifiziert ? "erreicht" : scopeOffen ? "aktiv" : mitScope.length === 0 ? "kein_signal" : "ausstehend",
      scopeVerifiziert
        ? "Schreibbereich (Scope) verifiziert."
        : scopeOffen
          ? "Schreibbereich noch nicht verifiziert."
          : "Kein Scope-Signal an den Teilaufgaben.",
    ),
    schritt(
      "ready",
      "Startbereit",
      bereit || laeuft ? "erreicht" : "ausstehend",
      bereit ? `Zwischenzustand erreicht (${bereit}).` : laeuft ? "Item läuft bereits." : "Noch kein Item startbereit geworden.",
    ),
    schritt(
      "lease",
      "Writer-Lease",
      leaseItem ? "erreicht" : laeuft ? "ausstehend" : "ausstehend",
      leaseItem
        ? `Writer: ${String(leaseItem.lease_holder)}`
        : "Noch keine exklusive Schreibberechtigung vergeben.",
      leaseItem?.lease_acquired_at ?? null,
    ),
    schritt(
      "execution",
      "Ausführung",
      laeuft ? "aktiv" : startedZeit ? "erreicht" : "ausstehend",
      laeuft
        ? "Ein Writer arbeitet gerade an dieser Mission."
        : startedZeit
          ? fehlgeschlagen
            ? "Ausführung lief und wurde mit Fehler beendet."
            : "Ausführung wurde gestartet."
          : "Noch keine Ausführung gestartet.",
      startedZeit,
    ),
    schritt(
      "tests",
      "Tests",
      testKind ? "erreicht" : "kein_signal",
      testKind
        ? `Test-Beleg vorhanden (${testKind}).`
        : "Diese Runtime meldet keinen separaten Test-Phasen-Signal — Belege siehe Evidence.",
    ),
    schritt(
      "verify",
      "Prüfung",
      verifiziertDurchFinding || verifizierterBeleg ? "erreicht" : "kein_signal",
      verifiziertDurchFinding || verifizierterBeleg
        ? verifizierterBeleg
          ? `Unabhängige Prüf-/Review-Spur belegt (Inhalt verifiziert: ${String(verifizierterBeleg.evidenceKind)}).`
          : "Unabhängige Prüf-/Review-Spur belegt."
        : pruefKind
          ? "Beleg-artiger Eintrag vorhanden, aber Inhalt nicht verifiziert (contentBinding ≠ VERIFIED_CONTENT)."
          : "Kein Verifier-/Review-Beleg verknüpft (Findings/Evidence).",
    ),
    schritt(
      "deploy",
      "Auslieferung",
      deployKind ? "erreicht" : "kein_signal",
      deployKind
        ? `Auslieferungs-Beleg vorhanden (${deployKind}).`
        : "Kein Deploy-/Release-Beleg verknüpft — ggf. reine Arbeits-Mission ohne Auslieferung.",
    ),
    schritt(
      "observe",
      "Beobachtung",
      observKind ? "erreicht" : "kein_signal",
      observKind
        ? `Beobachtungs-Beleg vorhanden (${observKind}).`
        : "Kein Beobachtungs-Beleg verknüpft.",
    ),
    schritt(
      "complete",
      "Abschluss",
      abgeschlossen || abschlussZeit ? "erreicht" : fehlgeschlagen ? "ausstehend" : "ausstehend",
      abgeschlossen || abschlussZeit
        ? "Mission abgeschlossen — Report/Evidence siehe gleichnamige Tabs."
        : fehlgeschlagen
          ? "Kein Abschluss — es liegt ein Fehlschlag vor."
          : "Mission noch nicht abgeschlossen.",
      abschlussZeit,
    ),
  ];
}

export const PHASEN_ZUSTAND_LABEL: Record<PhasenZustand, string> = {
  erreicht: "erreicht",
  aktiv: "läuft",
  ausstehend: "ausstehend",
  kein_signal: "kein separates Signal",
};
